import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { authApi } from '../api/auth';
import { ToastProvider } from './ToastContext';
import ToastContainer from '../components/ui/Toast';
import type { MeUserProfile } from '../types/auth';

interface AuthState {
  user: MeUserProfile | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children?: ReactNode }) {
  const [user, setUser] = useState<MeUserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await authApi.me();
      setUser(res.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email: string, password: string) => {
    const res = await authApi.login({ email, password });
    // After login, fetch full user with project roles
    setUser({ ...res.user, projects: [] });
    await refresh();
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh }}>
      <ToastProvider>
        <ToastContainer />
        {children ?? <Outlet />}
      </ToastProvider>
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
